// ForgotPassword.tsx
// Page de demande de réinitialisation de mot de passe.
// L'utilisateur saisit son email et reçoit un lien de réinitialisation par Supabase.
// Updated 2026-04-07: remplacement du titre texte "Qollect" par le logo SVG.

import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card from '../components/ui/Card';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email) {
      setError('Veuillez saisir votre adresse email');
      setLoading(false);
      return;
    }

    const { error: resetError } = await resetPassword(email);

    if (resetError) {
      setError('Une erreur est survenue. Veuillez réessayer.');
      setLoading(false);
      return;
    }

    setSent(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-soft-xl">
        <div className="text-center mb-10">
          <img src="/logo-qollect.svg" alt="Qollect" className="h-[50px] w-auto mx-auto mb-4" />
          <p className="text-neutral-500 text-lg">
            {sent ? 'Email envoyé' : 'Mot de passe oublié'}
          </p>
        </div>

        {sent ? (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-success-50 rounded-2xl flex items-center justify-center">
                <MailCheck className="w-8 h-8 text-success-600" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-neutral-700 font-medium">
                Un email vous a été envoyé
              </p>
              <p className="text-sm text-neutral-500 leading-relaxed">
                Si un compte existe pour <span className="font-medium text-neutral-700">{email}</span>, vous recevrez un lien pour réinitialiser votre mot de passe dans quelques instants.
              </p>
            </div>
            <p className="text-xs text-neutral-400">
              Pensez à vérifier vos spams si vous ne trouvez pas l'email.
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-500 mb-6 text-center leading-relaxed">
              Saisissez l'adresse email associée à votre compte. Nous vous enverrons un lien pour créer un nouveau mot de passe.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl font-medium">
                  {error}
                </div>
              )}

              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />

              <Button
                type="submit"
                fullWidth
                size="md"
                loading={loading}
              >
                Envoyer le lien de réinitialisation
              </Button>
            </form>
          </>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la connexion
          </Link>
        </div>
      </Card>
    </div>
  );
}
